import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { ArticleFragmentFragment } from '../../__generated__/graphql';
import { Heading3, Heading4, TextLink } from '../../components/Text';
import { useHeaderDispatch } from '../../layout/Header/state/hooks';
import { setSessionStorage } from '../../utils/sessionStorage';
import AppImage from '../AppImage';

import style from './article-card.module.css';

type ArticleCardSize = 'large' | 'medium' | 'small';

interface ArticleCardProps {
  article: ArticleFragmentFragment;
  articleLinkText: string;
  size: ArticleCardSize;
}

const articleCardSizeMap: Record<ArticleCardSize, string> = {
  large: style.articleCardLarge,
  medium: style.articleCardMedium,
  small: style.articleCardSmall,
};

const ArticleCard = ({
  article,
  articleLinkText,
  size,
}: ArticleCardProps) => {
  const router = useRouter();

  const category = article.categories?.nodes[0]?.name;

  const headerDispatch = useHeaderDispatch();

  const handleClearQueryString = useCallback(() => {
    if (!router.asPath.includes('blog')) {
      setSessionStorage('blogIndexQueryParams', '');

      headerDispatch({
        payload: {
          blogIndexQueryString: '',
        },
        type: 'updateBlogIndexQueryString',
      });
    }
  }, [headerDispatch, router.asPath]);

  return (
    <article
      className={classNames(style.articleCard, articleCardSizeMap[size])}
    >
      <Link
        aria-label={article.title ?? 'View article'}
        className={style.articleImageLink}
        href={article.uri ?? ''}
        onClick={handleClearQueryString}
      >
        {article.featuredImage && (
          <AppImage
            className={style.articleImage}
            fallbackHeight={387}
            fallbackWidth={620}
            image={article.featuredImage}
            withBorder
          />
        )}
      </Link>

      <div className={style.articleText}>
        {/* this div is used to push articleLinkText to the bottom of the container  */}
        <div>
          {category && (
            <Heading4
              className={style.articleType}
              fontColor="iris"
              fontSizeOverride={12}
              uppercase
            >
              {category}
            </Heading4>
          )}

          <Heading3 className={style.articleTitleLinkHeading}>
            <TextLink
              className={style.articleTitleLink}
              fontWeight={700}
              linkFragment={{
                title: article.title,
                url: article.uri ?? '',
              }}
              onClick={handleClearQueryString}
            />
          </Heading3>
        </div>

        {articleLinkText && (
          <TextLink
            fontSize={12}
            fontWeight={700}
            linkFragment={{
              title: articleLinkText,
              url: article.uri,
            }}
            onClick={handleClearQueryString}
            uppercase
          />
        )}
      </div>
    </article>
  );
};

export default ArticleCard;
